import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  MessageCircle, X, Send, Loader2, Sparkles,
  PhoneCall, ArrowLeft, CheckCircle2, User, Mail, Phone, MessageSquare, Bot, RotateCcw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Note: pas de localStorage — la conversation est réinitialisée à chaque rechargement / fermeture

const SUGGESTIONS = [
  { label: "Quelles formations ?", emoji: "🎓" },
  { label: "Comment candidater ?", emoji: "✍️" },
  { label: "Quand sont les rentrées ?", emoji: "📅" },
  { label: "Vos campus dans le monde ?", emoji: "🌍" },
];

function renderMessage(text) {
  if (!text) return null;
  const html = text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[\s(])\*(?!\s)([^*\n]+?)\*(?=[\s.,;:)!?]|$)/g, "$1<em>$2</em>")
    .replace(/\n/g, "<br/>");
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

// Small reusable avatar
const KimAvatar = ({ size = "md", withRing = true }) => {
  const dims = size === "lg" ? "w-12 h-12 text-2xl" : size === "sm" ? "w-7 h-7 text-xs" : "w-9 h-9 text-base";
  return (
    <div className={`relative shrink-0 ${dims}`}>
      {withRing && <div className="absolute inset-0 rounded-full bg-red-500/20 animate-ping" />}
      <div className={`relative ${dims} bg-gradient-to-br from-white to-[#e6ecf7] text-navy-700 flex items-center justify-center font-display font-semibold rounded-full ring-2 ring-red-500 shadow-[0_4px_12px_rgba(0,35,98,0.25)]`}>
        K
      </div>
    </div>
  );
};

// Floating label field — defined OUTSIDE the parent component to keep stable identity
// (otherwise React unmounts/remounts on each keystroke → input loses focus)
const FloatingField = ({
  id, type = "text", value, onChange, icon: Icon, label, required, placeholder, rows,
  focusedField, onFocus, onBlur,
}) => {
  const isFilled = value && value.length > 0;
  const isFocused = focusedField === id;
  const isActive = isFilled || isFocused;
  const Cmp = rows ? "textarea" : "input";
  return (
    <div className="relative">
      <label
        htmlFor={id}
        className={`absolute left-9 pointer-events-none transition-all duration-200 ${
          isActive
            ? "top-1.5 text-[9px] uppercase tracking-[0.18em] font-semibold text-navy-500"
            : "top-1/2 -translate-y-1/2 text-[13px] text-[#999]"
        }`}
      >
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <Icon
        className={`absolute left-3 top-4 h-3.5 w-3.5 transition-colors ${isActive ? "text-red-500" : "text-[#aaa]"}`}
        strokeWidth={1.8}
      />
      <Cmp
        id={id}
        type={type}
        value={value}
        rows={rows}
        onChange={onChange}
        onFocus={() => onFocus(id)}
        onBlur={() => onBlur(null)}
        placeholder={isActive ? placeholder : ""}
        required={required}
        data-testid={id}
        maxLength={rows ? 1000 : 200}
        className={`peer w-full ${rows ? "pt-6 pb-3" : "h-[52px] pt-4"} pl-9 pr-3 text-[13.5px] text-navy-700 bg-[#FAF8F3] border border-transparent rounded-md focus:bg-white focus:border-navy-300 focus:outline-none focus:ring-2 focus:ring-navy-500/10 transition-all ${rows ? "resize-none" : ""}`}
      />
    </div>
  );
};

export const KimChatbot = () => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("chat");
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [unseen, setUnseen] = useState(false);

  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [focusField, setFocusField] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending]);

  useEffect(() => {
    if (open) {
      setUnseen(false);
      if (messages.length === 0) {
        setMessages([{
          role: "assistant",
          content: "Bonjour ! 👋 Je suis **Kim**, l'assistante virtuelle d'ECL Campus Abidjan. Posez-moi vos questions sur les formations, l'admission, la vie étudiante… Je suis là pour vous guider.",
        }]);
      }
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]); // eslint-disable-line

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || sending) return;
    setMessages((m) => [...m, { role: "user", content: trimmed }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionId || null,
        message: trimmed,
      });
      if (!sessionId) {
        setSessionId(data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      if (!open) setUnseen(true);
    } catch (err) {
      setMessages((m) => [...m, {
        role: "assistant",
        content: "Désolée, je n'arrive pas à répondre pour le moment. Réessayez ou contactez-nous via la page Contact.",
      }]);
    } finally { setSending(false); }
  };

  const submitContactForm = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Nom et email requis");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: "Demande de contact via Kim",
        message: form.message.trim() || "Demande de prise de contact via l'assistante virtuelle Kim.",
        type: "kim_chatbot",
        phone: form.phone.trim() || null,
      });
      setView("success");
      toast.success("Demande envoyée !");
    } catch (err) {
      toast.error("Erreur", { description: "Veuillez réessayer dans un instant." });
    } finally { setSubmitting(false); }
  };

  const resetForm = () => {
    setForm({ name: "", email: "", phone: "", message: "" });
    setView("chat");
  };

  // Réinitialiser entièrement la conversation
  const resetConversation = () => {
    if (sending || submitting) return;
    setMessages([{
      role: "assistant",
      content: "Bonjour ! 👋 Je suis **Kim**, l'assistante virtuelle d'ECL Campus Abidjan. Posez-moi vos questions sur les formations, l'admission, la vie étudiante… Je suis là pour vous guider.",
    }]);
    setSessionId("");
    setInput("");
    setForm({ name: "", email: "", phone: "", message: "" });
    setView("chat");
    toast.success("Conversation réinitialisée");
  };

  return (
    <>
      {/* ───────── LAUNCHER (premium) ───────── */}
      <AnimatePresence>
        {!open && (
          <motion.div
            initial={{ scale: 0, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0, opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 200, damping: 18 }}
            className="fixed bottom-5 right-5 md:bottom-7 md:right-7 z-[60]"
          >
            {/* Tooltip teasing */}
            <motion.div
              initial={{ opacity: 0, y: 8, x: 8 }}
              animate={{ opacity: 1, y: 0, x: 0 }}
              transition={{ delay: 1.2, duration: 0.5 }}
              className="absolute bottom-full right-0 mb-3 hidden md:flex items-center gap-2 bg-white border border-navy-100 shadow-[0_8px_24px_rgba(0,35,98,0.12)] px-3.5 py-2.5 whitespace-nowrap"
            >
              <span className="font-display text-[13px] text-navy-700">Une question ?</span>
              <span className="text-[11px] text-red-500 font-semibold uppercase tracking-wider">Discutez avec Kim</span>
              <span className="absolute -bottom-1.5 right-6 w-3 h-3 bg-white border-r border-b border-navy-100 rotate-45" />
            </motion.div>

            <button
              onClick={() => setOpen(true)}
              data-testid="kim-launcher"
              aria-label="Ouvrir le chat avec Kim"
              className="relative group w-16 h-16 md:w-[68px] md:h-[68px] flex items-center justify-center"
            >
              {/* Pulse rings */}
              <span className="absolute inset-0 rounded-full bg-red-500/30 animate-ping" />
              <span className="absolute inset-1 rounded-full bg-red-500/20" />
              {/* Main bubble */}
              <span className="relative w-14 h-14 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-navy-500 via-navy-700 to-[#000e27] text-white flex items-center justify-center shadow-[0_12px_40px_rgba(0,35,98,0.55)] group-hover:scale-105 transition-transform duration-300 ring-2 ring-white">
                <MessageCircle className="h-6 w-6 md:h-7 md:w-7" strokeWidth={1.8} />
                {/* Online green dot */}
                <span className="absolute bottom-1 right-1 w-3 h-3 rounded-full bg-emerald-500 ring-2 ring-white">
                  <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-75" />
                </span>
                {/* Sparkle */}
                <Sparkles className="absolute -top-1 -right-1 h-3.5 w-3.5 text-red-500 fill-red-500" />
              </span>
              {/* Kim badge */}
              <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-[0.22em] font-bold bg-white text-navy-700 px-2 py-0.5 border border-navy-100 shadow-sm">
                Kim
              </span>
              {unseen && (
                <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-red-500 rounded-full ring-2 ring-white animate-pulse" />
              )}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ───────── CHAT PANEL ───────── */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            data-testid="kim-panel"
            className="fixed bottom-4 right-4 left-4 md:left-auto md:bottom-7 md:right-7 z-[60] md:w-[430px] bg-white shadow-[0_30px_80px_-15px_rgba(0,35,98,0.45)] flex flex-col overflow-hidden rounded-xl"
            style={{ maxHeight: "min(720px, calc(100vh - 2rem))" }}
          >
            {/* ───────── HEADER ───────── */}
            <div className="relative overflow-hidden bg-gradient-to-br from-navy-500 via-navy-700 to-[#000e27] text-white">
              {/* Decorative blurred orbs */}
              <div className="absolute -top-12 -right-10 w-40 h-40 bg-red-500/20 blur-3xl rounded-full pointer-events-none" />
              <div className="absolute -bottom-16 -left-10 w-44 h-44 bg-navy-500/30 blur-3xl rounded-full pointer-events-none" />
              {/* Pattern dots */}
              <svg className="absolute inset-0 w-full h-full opacity-[0.07] pointer-events-none" preserveAspectRatio="none">
                <defs>
                  <pattern id="kim-dots" width="14" height="14" patternUnits="userSpaceOnUse">
                    <circle cx="2" cy="2" r="1" fill="white" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#kim-dots)" />
              </svg>

              <div className="relative px-5 pt-5 pb-5 flex items-start gap-3.5">
                <KimAvatar size="lg" withRing={true} />
                <div className="flex-1 min-w-0 pt-0.5">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-display text-[22px] leading-none">Kim</span>
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-semibold bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 border border-emerald-400/30">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      en ligne
                    </span>
                  </div>
                  <div className="text-[11px] tracking-[0.18em] uppercase text-white/70 leading-tight">
                    {view === "form" ? "Demande de contact" : view === "success" ? "Demande reçue" : "Assistante virtuelle"}
                  </div>
                  <div className="text-[12px] text-white/60 mt-1 flex items-center gap-1.5">
                    <Bot className="h-3 w-3" strokeWidth={1.8} />
                    Propulsée par l'IA · Réponse instantanée
                  </div>
                </div>
                <button
                  onClick={resetConversation}
                  aria-label="Réinitialiser la conversation"
                  title="Nouvelle conversation"
                  data-testid="kim-reset"
                  className="text-white/60 hover:text-white hover:bg-white/10 transition-colors p-1.5 rounded-md"
                  disabled={sending || submitting}
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Fermer"
                  data-testid="kim-close"
                  className="text-white/60 hover:text-white hover:bg-white/10 transition-colors p-1.5 rounded-md"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Tricolor accent stripe */}
              <div className="relative h-[3px] flex">
                <span className="flex-1 bg-[#002362]" />
                <span className="flex-1 bg-white" />
                <span className="flex-1 bg-[#ED2939]" />
              </div>
            </div>

            {/* ───────── VIEW: CHAT ───────── */}
            {view === "chat" && (
              <>
                <div
                  ref={listRef}
                  data-testid="kim-messages"
                  className="flex-1 overflow-y-auto px-4 py-5 space-y-4 bg-[#FAF8F3]"
                  style={{
                    minHeight: 300,
                    backgroundImage:
                      "radial-gradient(circle at 20% 30%, rgba(0,35,98,0.04) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(237,41,57,0.03) 0%, transparent 50%)",
                  }}
                >
                  {messages.map((m, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.35 }}
                      className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      {m.role === "assistant" && <KimAvatar size="sm" withRing={false} />}
                      <div
                        data-testid={`kim-msg-${m.role}-${i}`}
                        className={`max-w-[78%] px-4 py-2.5 text-[14px] leading-relaxed shadow-sm ${
                          m.role === "user"
                            ? "bg-gradient-to-br from-navy-500 to-navy-700 text-white rounded-2xl rounded-br-sm"
                            : "bg-white text-navy-700 border border-navy-100 rounded-2xl rounded-bl-sm"
                        }`}
                      >
                        {renderMessage(m.content)}
                      </div>
                    </motion.div>
                  ))}
                  {sending && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-end gap-2 justify-start"
                      data-testid="kim-typing"
                    >
                      <KimAvatar size="sm" withRing={false} />
                      <div className="bg-white border border-navy-100 px-4 py-3 rounded-2xl rounded-bl-sm flex items-center gap-1.5 shadow-sm">
                        <span className="w-1.5 h-1.5 bg-navy-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-1.5 h-1.5 bg-navy-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-1.5 h-1.5 bg-navy-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Suggestions (emoji chips) */}
                {messages.length <= 1 && !sending && (
                  <div className="px-4 pt-3 pb-2 bg-white border-t border-navy-100">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2 flex items-center gap-1.5">
                      <Sparkles className="h-3 w-3 text-red-500" /> Suggestions
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {SUGGESTIONS.map((s) => (
                        <button
                          key={s.label}
                          onClick={() => send(s.label)}
                          data-testid={`kim-suggestion-${s.label.slice(0, 10).replace(/\s/g, "-")}`}
                          className="group flex items-center gap-1.5 text-[12px] px-3 py-1.5 bg-[#FAF8F3] border border-navy-100 rounded-full text-navy-700 hover:border-navy-500 hover:bg-white hover:shadow-sm transition-all"
                        >
                          <span>{s.emoji}</span>
                          <span>{s.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* STICKY CONTACT CTA (premium gradient) */}
                <motion.button
                  type="button"
                  onClick={() => setView("form")}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  data-testid="kim-contact-cta"
                  className="relative group overflow-hidden flex items-center justify-between gap-3 px-4 py-3.5 bg-gradient-to-r from-red-500 via-red-500 to-red-600 text-white"
                >
                  {/* Shine sweep on hover */}
                  <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/25 to-transparent" />
                  <span className="relative flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-white/15 backdrop-blur flex items-center justify-center ring-1 ring-white/30">
                      <PhoneCall className="h-4 w-4" strokeWidth={2} />
                    </span>
                    <span className="flex flex-col items-start leading-tight">
                      <span className="text-[12px] uppercase tracking-[0.14em] font-bold">Être recontacté(e)</span>
                      <span className="text-[10.5px] text-white/85 font-normal normal-case tracking-normal">Un conseiller vous appelle sous 48h</span>
                    </span>
                  </span>
                  <span className="relative text-lg group-hover:translate-x-1.5 transition-transform">→</span>
                </motion.button>

                {/* Input */}
                <form
                  onSubmit={(e) => { e.preventDefault(); send(); }}
                  className="flex items-center gap-2 px-3 py-2.5 border-t border-navy-100 bg-white"
                >
                  <div className="flex-1 relative">
                    <input
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder="Posez votre question à Kim…"
                      data-testid="kim-input"
                      className="w-full bg-[#FAF8F3] text-[14px] text-navy-700 placeholder:text-[#aaa] outline-none border border-transparent focus:border-navy-300 focus:bg-white px-3.5 h-11 rounded-full transition-colors"
                      disabled={sending}
                      maxLength={2000}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={sending || !input.trim()}
                    data-testid="kim-send"
                    aria-label="Envoyer"
                    className="w-11 h-11 bg-gradient-to-br from-navy-500 to-navy-700 text-white hover:from-navy-700 hover:to-[#000e27] transition-all disabled:opacity-40 flex items-center justify-center rounded-full shadow-md disabled:shadow-none hover:shadow-lg"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </button>
                </form>
              </>
            )}

            {/* ───────── VIEW: CONTACT FORM ───────── */}
            {view === "form" && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                className="flex-1 overflow-y-auto bg-white flex flex-col"
              >
                <div className="px-5 py-3 border-b border-navy-100 flex items-center justify-between bg-[#FAF8F3]">
                  <button
                    onClick={() => setView("chat")}
                    data-testid="kim-form-back"
                    className="text-navy-500 hover:text-navy-700 transition-colors flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" /> Retour
                  </button>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-red-500 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                    Étape 1 / 1
                  </div>
                </div>

                <form onSubmit={submitContactForm} data-testid="kim-contact-form" className="px-5 py-5 space-y-3.5 flex-1">
                  <div className="text-center mb-4 pb-4 border-b border-dashed border-navy-100">
                    <div className="inline-flex items-center justify-center w-11 h-11 bg-gradient-to-br from-red-500 to-red-600 text-white rounded-full mb-3 shadow-md">
                      <PhoneCall className="h-4.5 w-4.5" strokeWidth={1.8} />
                    </div>
                    <h3 className="font-display text-xl text-navy-700 leading-tight mb-1">
                      Laissez-nous vos coordonnées
                    </h3>
                    <p className="text-[12.5px] text-[#666] leading-relaxed">
                      Notre équipe vous recontacte sous <strong className="text-navy-700">48h</strong>.
                    </p>
                  </div>

                  <FloatingField
                    id="kim-form-name"
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    icon={User}
                    label="Nom complet"
                    required
                    placeholder="Jean Diallo"
                    focusedField={focusField}
                    onFocus={setFocusField}
                    onBlur={setFocusField}
                  />

                  <FloatingField
                    id="kim-form-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    icon={Mail}
                    label="Email"
                    required
                    placeholder="jean@example.com"
                    focusedField={focusField}
                    onFocus={setFocusField}
                    onBlur={setFocusField}
                  />

                  <FloatingField
                    id="kim-form-phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    icon={Phone}
                    label="Téléphone"
                    placeholder="+225 XX XX XX XX XX"
                    focusedField={focusField}
                    onFocus={setFocusField}
                    onBlur={setFocusField}
                  />

                  <FloatingField
                    id="kim-form-message"
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    icon={MessageSquare}
                    label="Votre message"
                    placeholder="Quelle formation vous intéresse ?"
                    focusedField={focusField}
                    onFocus={setFocusField}
                    onBlur={setFocusField}
                  />

                  <motion.button
                    type="submit"
                    disabled={submitting}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid="kim-form-submit"
                    className="relative group overflow-hidden w-full bg-gradient-to-r from-red-500 to-red-600 text-white py-3.5 text-[12px] uppercase tracking-[0.15em] font-bold transition-all disabled:opacity-60 flex items-center justify-center gap-2 shadow-md hover:shadow-xl rounded-md"
                  >
                    <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/25 to-transparent" />
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin relative" /> : <Send className="h-4 w-4 relative" />}
                    <span className="relative">{submitting ? "Envoi en cours…" : "Envoyer ma demande"}</span>
                  </motion.button>

                  <p className="text-[10.5px] text-[#888] leading-relaxed text-center pt-2">
                    🔒 En soumettant, vous acceptez notre{" "}
                    <a href="/politique-de-confidentialite" className="text-navy-700 underline underline-offset-2 hover:text-red-500 font-semibold">
                      politique de confidentialité
                    </a>.
                  </p>
                </form>
              </motion.div>
            )}

            {/* ───────── VIEW: SUCCESS ───────── */}
            {view === "success" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex-1 flex flex-col items-center justify-center text-center px-6 py-10 relative overflow-hidden bg-gradient-to-b from-[#FAF8F3] to-white"
                data-testid="kim-form-success"
              >
                {/* Floating confetti */}
                {[...Array(8)].map((_, i) => (
                  <motion.span
                    key={i}
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: [0, -120, -180], opacity: [0, 1, 0], x: [(i - 4) * 12, (i - 4) * 30] }}
                    transition={{ duration: 2 + i * 0.1, delay: 0.3 + i * 0.05, repeat: Infinity, repeatDelay: 3 }}
                    className={`absolute text-base pointer-events-none ${i % 2 ? "text-red-500" : "text-navy-500"}`}
                    style={{ top: "55%", left: `${15 + i * 9}%` }}
                  >
                    {["✦", "✧", "✨", "★", "✩", "✪", "✫", "✬"][i]}
                  </motion.span>
                ))}

                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
                  className="relative w-20 h-20 mb-6"
                >
                  <span className="absolute inset-0 rounded-full bg-emerald-500/20 animate-ping" />
                  <div className="relative w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 text-white flex items-center justify-center rounded-full shadow-[0_12px_30px_rgba(16,185,129,0.4)] ring-4 ring-white">
                    <CheckCircle2 className="h-9 w-9" strokeWidth={2} />
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <div className="overline mb-3">Demande envoyée</div>
                  <h3 className="font-display text-2xl text-navy-700 mb-3 leading-tight">
                    Merci, <em className="not-italic red-shine">{form.name.split(" ")[0] || "à vous"}</em> !
                  </h3>
                  <p className="text-[#555] text-[14px] leading-relaxed mb-2 max-w-xs">
                    Notre équipe vous recontacte à
                  </p>
                  <p className="text-navy-700 font-semibold text-[14px] mb-6 break-all">
                    {form.email}
                  </p>
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-navy-50 border border-navy-100 text-navy-700 text-[11px] uppercase tracking-[0.18em] font-semibold mb-6">
                    <Sparkles className="h-3 w-3 text-red-500" /> Sous 48 heures
                  </div>
                  <br />
                  <button
                    onClick={resetForm}
                    data-testid="kim-form-back-to-chat"
                    className="text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-700 hover:text-red-500 transition-colors border-b border-navy-200 hover:border-red-500 pb-0.5"
                  >
                    ← Continuer à discuter avec Kim
                  </button>
                </motion.div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default KimChatbot;
